import { BigDollarInput } from "@/components/BigDollarInput/BigDollarInput";
import { Doc } from "@/components/Doc/Doc";
import { Icons } from "@/components/Icons/Icons";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import {
  buyerAnnualCapExAtom,
  growthExpenseItemsAtom,
  isGrowthCapExIncludedAtom,
} from "@/financeModels/owasco/general";
import { toNumberOrNull } from "@/utils/math";
import { formatMoney } from "@/utils/moneyFormating";
import {
  ActionIcon,
  Button,
  Card,
  Group,
  NumberInput,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { useAtom } from "jotai";
import { ChangeEvent } from "react";
import { LuDot } from "react-icons/lu";
import { TbFileInvoice } from "react-icons/tb";
import { InputDollar } from "../../Sidebar/InputDollar";
import { InputToggle } from "../../Sidebar/InputToggle";

const Panel = () => {
  const [capEx, setCapEx] = useAtom(buyerAnnualCapExAtom);
  const [isGrowthCapExIncluded, setGrowthCapExIncluded] = useAtom(
    isGrowthCapExIncludedAtom,
  );

  return (
    <Stack gap={4}>
      <InputDollar
        value={toNumberOrNull(capEx) || ""}
        onChange={(value: number | null) => setCapEx(toNumberOrNull(value))}
        label={
          <LabelExplainer
            label="Maintenance CapEx"
            explanation={
              <Doc path="@/docs/pages/model/sidebar/opAssMaintenanceCapEx.mdx" />
            }
          />
        }
      />
      <InputToggle
        label={
          <LabelExplainer
            label="Enable Growth CapEx"
            explanation={
              <Doc path="@/docs/pages/model/sidebar/capExIsGrowthCapExIncluded.mdx" />
            }
          />
        }
        checked={isGrowthCapExIncluded}
        onChange={(event) => {
          setGrowthCapExIncluded(
            (event as ChangeEvent<HTMLInputElement>).currentTarget.checked,
          );
        }}
      />

      {isGrowthCapExIncluded && <List />}
    </Stack>
  );
};

export const List = () => {
  const [items] = useAtom(growthExpenseItemsAtom);

  return (
    <Card withBorder p={"sm"}>
      <Stack>
        {items.map((item, idx) => (
          <Group justify="space-between" align="center" key={idx} gap={0}>
            <Stack gap={0}>
              <Text
                size="md"
                lh={1.2}
                fw={500}
                style={{
                  display: "inline-block",
                  maxWidth: 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {item.name}
              </Text>
              <Group gap={0} align="center" w={240}>
                <Text size="sm" fw={500}>
                  {formatMoney(item.amount).replace(".00", "")}
                </Text>
                <LuDot />
                <Text size="xs">
                  Year {item.start} start, {item.duration} years (D&A)
                </Text>
              </Group>
            </Stack>
            <ActionIcon
              variant="subtle"
              color="blue"
              size={"lg"}
              onClick={() => {
                modals.open({
                  title: "Edit Growth Capital Expenditure",
                  children: (
                    <AddExpenditureModal
                      initialValues={item}
                      editId={item.id}
                    />
                  ),
                });
              }}
            >
              <Icons.Edit size={18} />
            </ActionIcon>
          </Group>
        ))}
        <Button
          onClick={() => {
            modals.open({
              title: "Add Growth Capital Expenditure",
              children: <AddExpenditureModal />,
            });
          }}
        >
          Add Growth CapEx
        </Button>
      </Stack>
    </Card>
  );
};

type AddExpenditureModalProps = {
  initialValues?: {
    name?: string;
    amount?: number | string;
    start?: number;
    duration?: number;
  };
  editId?: string;
};

const AddExpenditureModal = ({
  initialValues = {},
  editId,
}: AddExpenditureModalProps) => {
  const [, setItems] = useAtom(growthExpenseItemsAtom);

  console.log({
    initialValues,
  });

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      name: initialValues.name ?? "",
      amount: initialValues.amount ?? 10000,
      start: initialValues.start ?? 1,
      duration: initialValues.duration ?? 10,
    },
    validate: {
      name: (value: string) => (value ? null : "Name is required"),
      amount: (value: number | string) =>
        value && !isNaN(Number(value)) ? null : "Amount must be a number",
      start: (value: number) =>
        value && !isNaN(Number(value)) ? null : "Start year must be a number",
      duration: (value: number) =>
        value && !isNaN(Number(value)) ? null : "Duration must be a number",
    },
  });

  const onSubmit = (values: typeof form.values) => {
    const newItem = {
      id: Date.now().toString(),
      name: values.name,
      amount: Number(values.amount),
      start: values.start,
      duration: values.duration,
    };

    if (editId) {
      setItems((prev) =>
        prev.map((item) =>
          item.id === editId ? { ...item, ...newItem } : item,
        ),
      );
    } else {
      setItems((prev) => [...prev, newItem]);
    }

    modals.closeAll();
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap={"xs"}>
        <TextInput
          label="Expenditure Name"
          placeholder="Enter name"
          {...form.getInputProps("name")}
          error={form.errors.name}
        />
        <Group>
          <BigDollarInput
            w={148}
            size="sm"
            label="Amount"
            {...form.getInputProps("amount")}
            value={form.values.amount}
            error={form.errors.amount}
          />
          <NumberInput
            w={110}
            label="Start On Year"
            {...form.getInputProps("start")}
            error={form.errors.start}
          />
          <NumberInput
            w={110}
            label="Duration (years)"
            {...form.getInputProps("duration")}
            error={form.errors.duration}
          />
        </Group>
        <Stack gap={0}>
          <Button mt={20} type="submit">
            {editId ? "Update Expenditure" : "Add Expenditure"}
          </Button>
          {editId && (
            <Button
              mt={10}
              variant="outline"
              color="red"
              onClick={() => {
                setItems((prev) => prev.filter((item) => item.id !== editId));
                modals.closeAll();
              }}
              leftSection={<Icons.Delete size={18} />}
            >
              Delete Expenditure
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  );
};

export const ModuleCapitalExpenditures = {
  key: "Capital Expenditures (CapEx)",
  icon: <TbFileInvoice />,
  control: "Capital Expenditures (CapEx)",
  panel: <Panel />,
};
